import DOMPurify from 'dompurify';
import Token from 'markdown-it/lib/token.mjs';
import { createMarkdownRenderer } from '@/utils/md-to-html/createMarkdownRenderer';
import { embedImages, throwIfAborted } from './exportImages';
import { maskSecretFields } from './maskSecretFields';
import type { ExportLabels, ExportProgress, NoteExportSnapshot } from './types';

const md = createMarkdownRenderer();
// Keep unsafe image references as tokens so they can become explicit placeholders.
// Hyperlinks and image bytes are validated separately before document assembly.
md.validateLink = () => true;
md.inline.ruler.before('autolink', 'export_image', (state, silent) => {
  const match = /^<img\b[^>]*\/?>/i.exec(state.src.slice(state.pos));
  if (!match) return false;
  if (!silent) {
    const template = document.createElement('template');
    template.innerHTML = match[0];
    const image = template.content.querySelector('img');
    const token = state.push('image', 'img', 0);
    token.attrs = [['src', image?.getAttribute('src') ?? ''], ['alt', image?.getAttribute('alt') ?? '']];
    token.content = image?.getAttribute('alt') ?? '';
    const alt = new Token('text', '', 0);
    alt.content = token.content;
    token.children = [alt];
    for (const name of ['width', 'height']) {
      const value = image?.getAttribute(name);
      if (value && /^\d{1,5}$/.test(value)) token.attrSet(name, value);
    }
  }
  state.pos += match[0].length;
  return true;
});

const bodyPolicy = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'em', 'u', 's', 'code', 'pre', 'a', 'ul', 'ol', 'li', 'blockquote',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
    'input', 'label', 'div', 'span', 'img',
  ],
  ALLOWED_ATTR: ['href', 'class', 'type', 'checked', 'disabled', 'src', 'alt', 'width', 'height', 'start'],
  ALLOW_DATA_ATTR: false,
};

const styles = `
:root{color-scheme:light}*{box-sizing:border-box}
body{margin:0;background:#fff;color:#171717;font:16px/1.6 system-ui,sans-serif}
article{max-width:52rem;margin:2rem auto;padding:0 1.5rem;overflow-wrap:anywhere}
h1,h2,h3,h4,h5,h6{line-height:1.25;break-after:avoid}
img{max-width:100%;height:auto;object-fit:contain;break-inside:avoid}
pre,code{font-family:ui-monospace,monospace}
pre{white-space:pre-wrap;overflow-wrap:anywhere;border:1px solid #ddd;padding:.75rem}
table{width:100%;border-collapse:collapse;table-layout:fixed}
th,td{border:1px solid #bbb;padding:.4rem;overflow-wrap:anywhere}
thead{display:table-header-group}tr{break-inside:avoid}
blockquote{border-left:3px solid #bbb;margin-left:0;padding-left:1rem}
a{color:#164e8a;overflow-wrap:anywhere}
.missing-image{display:block;border:1px dashed #888;padding:.75rem;margin:.5rem 0}
.version{color:#555;font-size:.875rem}
@page{margin:15mm}
@media print{body{font-size:11pt}article{max-width:none;margin:0;padding:0}
img{max-height:230mm}a{color:inherit}pre{overflow:visible}}
`;

export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[character]!);
}

export function exportFilename(title: string, fallback: string): string {
  const safe = title.replace(/[<>:"/\\|?*\u0000-\u001f\u007f]/g, '').trim().replace(/[. ]+$/g, '').slice(0, 100);
  return `${safe || fallback.replace(/[^a-zA-Z0-9_-]/g, '') || 'note'}.html`;
}

export async function prepareNoteHtml(
  snapshot: NoteExportSnapshot,
  labels: ExportLabels,
  signal: AbortSignal,
  onProgress: (progress: ExportProgress) => void,
): Promise<{ html: string; missingImages: string[] }> {
  throwIfAborted(signal);
  // Entity tokens keep public labels out of both Markdown and automatic link parsing.
  const safeBody = maskSecretFields(snapshot.body, `[${labels.secret}]`, (label) => snapshot.contentType === 'MDX'
    ? Array.from(label, (character) => `&#${character.codePointAt(0)};`).join('')
    : label);
  const markup = snapshot.contentType === 'MDX' ? md.render(safeBody) : `<pre>${escapeHtml(safeBody)}</pre>`;
  const template = document.createElement('template');
  // Temporary blob sources are permitted only in this inert, intermediate tree.
  // Every image is replaced with validated raster bytes before final sanitization.
  template.innerHTML = DOMPurify.sanitize(markup, { ...bodyPolicy, ADD_URI_SAFE_ATTR: ['src'] });
  template.content.querySelectorAll('a').forEach((link) => {
    const href = link.getAttribute('href') ?? '';
    try {
      const url = new URL(href, window.location.href);
      if (!['http:', 'https:', 'mailto:', 'tel:'].includes(url.protocol)) link.removeAttribute('href');
      else link.setAttribute('href', url.href);
    } catch {
      link.removeAttribute('href');
    }
  });
  template.content.querySelectorAll('input').forEach((input) => {
    if (input.getAttribute('type') !== 'checkbox') input.remove();
    else input.setAttribute('disabled', '');
  });
  const missingImages = await embedImages(template.content, signal, labels.missingImage, onProgress);
  const body = DOMPurify.sanitize(template.innerHTML, bodyPolicy);
  const title = escapeHtml(snapshot.title.trim() || labels.untitled);
  const version = snapshot.version === null ? '' : `<p class="version">${escapeHtml(labels.historicalVersion)}</p>`;
  throwIfAborted(signal);
  return {
    html: `<!doctype html>
<html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data:; style-src 'unsafe-inline'; base-uri 'none'; form-action 'none'">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="referrer" content="no-referrer">
<title>${title}</title><style>${styles}</style></head>
<body><article><h1>${title}</h1>${version}${body}</article></body></html>`,
    missingImages,
  };
}
